"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { type ReactNode, type ComponentProps } from "react";

import { cn } from "../lib/utils.js";

import { Dialog, DialogPortal, DialogOverlay } from "./dialog.js";
import { SECTION_ACCENTS, type SectionAccent } from "./section-card.js";

export type BoulderTone = SectionAccent;

interface BoulderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  /** Header band tint. Defaults to slate. */
  tone?: BoulderTone;
  /** When true, the dialog border + accent stripe go green to signal "ready to submit". */
  isComplete?: boolean;
  /** Header slot that renders BELOW the title row but ABOVE the body — used by FormCompletenessHeader. */
  headerExtra?: ReactNode;
  /** Body content. */
  children: ReactNode;
  /** Footer (action buttons). */
  footer?: ReactNode;
  /** Width preset. Mirrors common Tailwind max-w-* values used in the app. */
  width?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl" | "7xl";
  /** When true, body becomes a flex column with internal scroll — useful for tall forms. */
  scrollableBody?: boolean;
  /** Override classes on the outer DialogContent. */
  contentClassName?: string;
  /** Override classes on the body wrapper. */
  bodyClassName?: string;
}

const WIDTH_CLASS: Record<NonNullable<BoulderDialogProps["width"]>, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
};

/**
 * Boulder-themed dialog wrapper. Built on the existing Radix-backed Dialog
 * primitive — does NOT replace it — but renders a richer chrome:
 *   - Colored top accent stripe (matches SectionCard vocabulary)
 *   - Tinted header band with icon chip + bold title
 *   - Drop shadow with colored cast
 *   - Border that turns emerald-500 with a soft glow when `isComplete` is true
 *   - Optional `headerExtra` slot for FormCompletenessHeader
 *
 * Usage mirrors the AddActivityDialog migration: instead of a Dialog/DialogContent
 * stack, render <BoulderDialog open onOpenChange title icon footer>...</BoulderDialog>.
 */
export function BoulderDialog({
  open,
  onOpenChange,
  title,
  description,
  icon,
  tone = "slate",
  isComplete = false,
  headerExtra,
  children,
  footer,
  width = "2xl",
  scrollableBody = false,
  contentClassName,
  bodyClassName,
}: BoulderDialogProps) {
  const accent = SECTION_ACCENTS[tone];
  // When the form is complete, swap to an emerald palette + glow.
  const completeBorder = "border-emerald-400 ring-1 ring-emerald-300/60 shadow-emerald-200/50";
  const completeStripe = "bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay />
        {/* Use DialogPrimitive.Content directly (not the wrapping
            DialogContent from ./dialog.js) because that wrapper renders its
            own absolutely-positioned close button — which would collide with
            the styled close button we render in our header band. */}
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
            "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
            "duration-200 sm:rounded-lg",
            "p-0 gap-0 overflow-hidden border bg-white shadow-2xl",
            "w-full",
            WIDTH_CLASS[width],
            scrollableBody && "h-[85vh] flex flex-col",
            // Default border = accent color; complete = emerald
            isComplete ? completeBorder : accent.card,
            "transition-[border-color,box-shadow] duration-300",
            contentClassName,
          )}
        >
          {/* Top accent stripe — turns green when complete */}
          <div
            className={cn(
              "h-1.5 shrink-0 transition-colors duration-300",
              isComplete ? completeStripe : `bg-gradient-to-r ${gradientFor(tone)}`,
            )}
            aria-hidden
          />

          {/* Header band */}
          <div
            className={cn(
              "flex items-start justify-between gap-3 px-5 py-3 border-b shrink-0",
              isComplete ? "bg-emerald-50/70 border-emerald-200" : accent.header,
            )}
          >
            <div className="flex items-start gap-3 min-w-0">
              {icon && (
                <span
                  className={cn(
                    "shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-lg ring-1 ring-inset ring-black/5 shadow-sm",
                    isComplete ? "bg-emerald-100 text-emerald-700" : cn(accent.iconChip, accent.iconColor),
                  )}
                >
                  {icon}
                </span>
              )}
              <div className="min-w-0">
                {/* Radix a11y (card B-5): every DialogContent needs a
                    DialogPrimitive.Title and an aria-describedby, or Radix
                    logs "DialogContent requires a DialogTitle … Missing
                    Description or aria-describedby". We render our own styled
                    <h2> as the Title via asChild, and always emit a
                    Description — visible when `description` is set, otherwise
                    visually hidden so aria-describedby is still satisfied
                    without changing the visual chrome. */}
                <DialogPrimitive.Title asChild>
                  <h2
                    className={cn(
                      "text-lg font-bold leading-tight tracking-tight truncate",
                      isComplete ? "text-emerald-900" : accent.title,
                    )}
                  >
                    {title}
                  </h2>
                </DialogPrimitive.Title>
                {description ? (
                  <DialogPrimitive.Description className="mt-0.5 text-sm text-muted-foreground">
                    {description}
                  </DialogPrimitive.Description>
                ) : (
                  <DialogPrimitive.Description className="sr-only">
                    {typeof title === "string" ? title : "Dialog"}
                  </DialogPrimitive.Description>
                )}
              </div>
            </div>
            <DialogPrimitive.Close
              className="shrink-0 -mr-1 -mt-1 rounded-md p-1.5 text-muted-foreground hover:bg-black/5 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>

          {headerExtra && (
            <div className={cn("px-5 pt-3 pb-1 shrink-0", scrollableBody && "border-b border-slate-100")}>
              {headerExtra}
            </div>
          )}

          <div
            className={cn(
              scrollableBody ? "flex-1 min-h-0 overflow-y-auto" : "max-h-[70vh] overflow-y-auto",
              "px-5 py-4",
              bodyClassName,
            )}
          >
            {children}
          </div>

          {footer && (
            <div className="shrink-0 px-5 py-3 border-t bg-slate-50/60 flex items-center justify-end gap-2">
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

/** Tone -> tailwind gradient classes for the top accent stripe. */
function gradientFor(tone: BoulderTone): string {
  switch (tone) {
    case "indigo":
      return "from-indigo-400 via-indigo-500 to-indigo-600";
    case "blue":
      return "from-blue-400 via-blue-500 to-blue-600";
    case "emerald":
      return "from-emerald-400 via-emerald-500 to-emerald-600";
    case "amber":
      return "from-amber-300 via-amber-500 to-amber-600";
    case "rose":
      return "from-rose-400 via-rose-500 to-rose-600";
    case "purple":
      return "from-purple-400 via-purple-500 to-purple-600";
    case "slate":
    default:
      return "from-slate-500 via-slate-600 to-slate-700";
  }
}

/**
 * Footer-positioned primary action that visually reflects form completeness.
 * When `isComplete` is true, the button gets a green gradient + glow.
 */
export interface BoulderPrimaryButtonProps extends ComponentProps<"button"> {
  isComplete?: boolean;
  isPending?: boolean;
}

export function BoulderPrimaryButton({
  isComplete = false,
  isPending = false,
  className,
  children,
  disabled,
  ...rest
}: BoulderPrimaryButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || isPending}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold",
        "transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50",
        "focus:outline-none focus:ring-2 focus:ring-offset-1",
        isComplete
          ? "bg-gradient-to-b from-emerald-500 to-emerald-600 text-white shadow-md shadow-emerald-500/30 ring-1 ring-emerald-700/20 hover:from-emerald-500 hover:to-emerald-700 focus:ring-emerald-400"
          : "bg-gradient-to-b from-slate-700 to-slate-800 text-white shadow-sm ring-1 ring-slate-900/20 hover:from-slate-700 hover:to-slate-900 focus:ring-slate-400",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
